import { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ArrowLeft } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { productImage } from "@/lib/images";

export default function CartPage() {
  const { items, removeItem, updateQty, subtotal, clear } = useCart();
  const [customer, setCustomer] = useState({ name: "", email: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  const [discounts, setDiscounts] = useState([]); // [{code, percent_off, applies_to_size, free_delivery}]
  const [discountChecking, setDiscountChecking] = useState(false);

  const DELIVERY = 3.95;

  // Per item: sum percent_off of every applicable code, capped at 100.
  const discountAmount = items.reduce((sum, i) => {
    const totalPct = Math.min(
      100,
      discounts.reduce((p, d) => {
        if (!d.percent_off) return p;
        if (d.applies_to_size && i.size !== d.applies_to_size) return p;
        return p + d.percent_off;
      }, 0)
    );
    return sum + (i.price * i.quantity * totalPct) / 100;
  }, 0);
  const freeDelivery = discounts.some((d) => d.free_delivery);
  const deliveryCharge = freeDelivery ? 0 : (items.length ? DELIVERY : 0);
  const grandTotal = Math.max(0, subtotal - discountAmount) + deliveryCharge;

  const applyDiscount = async () => {
    const code = discountInput.trim().toLowerCase();
    if (!code) return;
    if (discounts.some((d) => d.code === code)) {
      toast.info("That code is already applied");
      setDiscountInput("");
      return;
    }
    setDiscountChecking(true);
    try {
      const { data } = await api.get(`/discount/check?code=${encodeURIComponent(code)}`);
      setDiscounts((arr) => [...arr, data]);
      setDiscountInput("");
      const label = data.free_delivery
        ? "free UK delivery"
        : `${data.percent_off}% off ${data.applies_to_size || "every animal"}`;
      toast.success(`Code applied — ${label}`);
    } catch {
      toast.error("Invalid discount code");
    } finally {
      setDiscountChecking(false);
    }
  };
  const removeDiscount = (code) => setDiscounts((arr) => arr.filter((d) => d.code !== code));

  const startCheckout = async () => {
    if (items.length === 0) return;
    if (!customer.name || !customer.email || !customer.address) {
      toast.error("Please fill in your shipping details");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          personalization: i.personalization || {},
        })),
        customer_email: customer.email,
        customer_name: customer.name,
        shipping_address: customer.address,
        origin_url: window.location.origin,
        discount_codes: discounts.map((d) => d.code),
      };
      const { data } = await api.post("/checkout/session", payload);
      if (data.url) {
        setRedirecting(data.url);
        // Break out of any iframe (e.g. Emergent preview pane) — Stripe Checkout
        // refuses to load inside iframes. window.top redirects the topmost frame.
        setTimeout(() => {
          try {
            if (window.top && window.top !== window.self) {
              window.top.location.href = data.url;
              return;
            }
          } catch (err) { /* cross-origin parent — fall through */
            console.warn("checkout: window.top access blocked, navigating self", err);
          }
          window.location.href = data.url;
        }, 300);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="container-x py-12 lg:py-16" data-testid="cart-page">
      {redirecting && (
        <div className="fixed inset-0 z-50 bg-white/95 backdrop-blur-sm flex items-center justify-center p-6" data-testid="redirecting-overlay">
          <div className="max-w-md text-center">
            <div className="w-12 h-12 mx-auto mb-6 rounded-full border-2 border-[#EAE3D9] border-t-[#D97757] animate-spin" />
            <div className="font-serif text-2xl text-[#2C211B]">Redirecting to secure payment…</div>
            <p className="mt-3 text-sm text-[#5C4D43] leading-relaxed">
              Stripe is preparing your checkout. This can take up to 15 seconds the first time — please don&apos;t close this tab.
            </p>
            <p className="mt-6 text-xs text-[#5C4D43]">
              Not loading? <a href={redirecting} target="_blank" rel="noopener noreferrer" className="underline text-[#D97757] font-medium">Open payment page in a new tab</a>
            </p>
          </div>
        </div>
      )}
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#5C4D43] hover:text-[#D97757]" data-testid="cart-back-shop">
        <ArrowLeft size={16} /> Keep browsing
      </Link>
      <h1 className="font-serif text-5xl lg:text-6xl mt-6">Your basket</h1>

      {items.length === 0 ? (
        <div className="mt-10 text-center py-20" data-testid="cart-empty">
          <div className="font-serif text-2xl text-[#2C211B]">Your basket is empty</div>
          <p className="mt-2 text-[#5C4D43]">Go find a little friend to take home.</p>
          <Link to="/shop" className="btn-primary inline-block mt-6">Browse the shop</Link>
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-[1fr_400px] gap-10">
          <div className="space-y-4" data-testid="cart-items">
            {items.map((i) => (
              <div key={i.line_id} className="card-soft p-4 flex gap-4" data-testid={`cart-item-${i.line_id}`}>
                <img src={productImage(i.image_url)} alt={i.name} className="w-24 h-24 rounded-2xl object-cover bg-[#F4EFE6]" />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-serif text-xl">{i.name}</div>
                      <div className="text-sm text-[#5C4D43]">£{i.price.toFixed(2)}</div>
                    </div>
                    <button
                      data-testid={`remove-${i.line_id}`}
                      onClick={() => removeItem(i.line_id)}
                      className="p-2 rounded-full hover:bg-[#F4EFE6] text-[#5C4D43]"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {i.personalization && Object.keys(i.personalization).length > 0 && (
                    <div className="mt-2 text-xs text-[#5C4D43] space-y-0.5">
                      {Object.entries(i.personalization).map(([k, v]) => (
                        <div key={k}><span className="font-medium capitalize">{k}:</span> {v}</div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-3 border border-[#EAE3D9] rounded-full px-2 py-1 w-fit">
                    <button data-testid={`dec-${i.line_id}`} onClick={() => updateQty(i.line_id, i.quantity - 1)} className="w-8 h-8 rounded-full hover:bg-[#F4EFE6]">–</button>
                    <span className="w-5 text-center text-sm">{i.quantity}</span>
                    <button data-testid={`inc-${i.line_id}`} onClick={() => updateQty(i.line_id, i.quantity + 1)} className="w-8 h-8 rounded-full hover:bg-[#F4EFE6]">+</button>
                  </div>
                </div>
              </div>
            ))}
            <button onClick={clear} className="text-sm text-[#5C4D43] hover:text-[#D97757]" data-testid="cart-clear">
              Clear basket
            </button>
          </div>

          <div className="card-soft p-6 lg:p-8 h-fit sticky top-28" data-testid="cart-summary">
            <div className="font-serif text-2xl">Shipping details</div>
            <div className="mt-5 space-y-3">
              <input data-testid="customer-name" className="input-warm" placeholder="Full name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
              <input data-testid="customer-email" className="input-warm" type="email" placeholder="Email address" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
              <textarea data-testid="customer-address" className="input-warm min-h-[100px]" placeholder="Shipping address" value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} />
            </div>
            <div className="mt-6 pt-6 border-t border-[#EAE3D9] space-y-2" data-testid="cart-totals">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#5C4D43]">Subtotal</span>
                <span data-testid="cart-subtotal">£{subtotal.toFixed(2)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex items-center justify-between text-sm text-[#A3B18A]">
                  <span>Discount</span>
                  <span data-testid="cart-discount">– £{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#5C4D43]">UK delivery — Royal Mail 2nd class</span>
                <span data-testid="cart-delivery">
                  {deliveryCharge === 0 ? <span className="text-[#A3B18A]">FREE</span> : `£${deliveryCharge.toFixed(2)}`}
                </span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-[#EAE3D9]">
                <span className="text-[#5C4D43]">Total</span>
                <span className="font-serif text-2xl" data-testid="cart-total">£{grandTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Discount code section — stackable: enter as many valid codes as you have */}
            <div className="mt-5 p-4 rounded-2xl bg-[#F4EFE6]/60 border border-[#EAE3D9]" data-testid="discount-section">
              <div className="text-xs uppercase tracking-[0.18em] text-[#5C4D43] mb-2">Discount codes</div>

              {discounts.length > 0 && (
                <ul className="space-y-1.5 mb-3" data-testid="discount-list">
                  {discounts.map((d) => (
                    <li
                      key={d.code}
                      data-testid={`discount-chip-${d.code}`}
                      className="flex items-center justify-between gap-3 text-sm bg-white/70 border border-[#EAE3D9] rounded-full px-3 py-1.5"
                    >
                      <span>
                        <span className="font-medium uppercase tracking-wider">{d.code}</span>
                        <span className="text-[#5C4D43]"> — {d.free_delivery
                          ? "free UK delivery"
                          : `${d.percent_off}% off ${d.applies_to_size || "every animal"}`}</span>
                      </span>
                      <button
                        data-testid={`discount-remove-${d.code}`}
                        onClick={() => removeDiscount(d.code)}
                        className="text-xs underline text-[#5C4D43] hover:text-[#D97757]"
                        aria-label={`Remove ${d.code}`}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex gap-2">
                <input
                  data-testid="discount-input"
                  className="input-warm flex-1"
                  placeholder="Enter another code"
                  value={discountInput}
                  onChange={(e) => setDiscountInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyDiscount(); } }}
                />
                <button
                  data-testid="discount-apply"
                  onClick={applyDiscount}
                  disabled={discountChecking || !discountInput.trim()}
                  className="btn-secondary disabled:opacity-60"
                >
                  {discountChecking ? "..." : "Apply"}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-[#5C4D43]">
                Have a code? Enter it above. You can apply more than one.
              </p>
            </div>
            <button
              data-testid="checkout-btn"
              onClick={startCheckout}
              disabled={busy}
              className="btn-primary w-full mt-6 disabled:opacity-60"
            >
              {busy ? "Preparing checkout… (takes ~10s)" : "Checkout with Stripe"}
            </button>
            <p className="text-xs text-[#5C4D43] mt-3 text-center">
              Prefer to chat first? <Link to="/contact" className="underline">Send us a message</Link>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
